import { inject, reactive, ref, watch } from 'vue'
import { useTemplateStore, useAuthStore, useBranchesStore } from 'src/stores'
import {
  IBaseTableDialogOptions,
  IItemSummary,
  ITableRow,
  ITemplateItemFieldValue,
  ITemplateLayerItem,
} from 'src/common/interfaces'
import translate from 'src/common/helpers/language/translate'
import globalTranslateObject from 'src/translate/global.translate'
import translationObject from '../../translate/baseTemplate.translate'
import { getImagePath } from 'src/common/helpers/generator/filePath.helper'
import { formatMonetaryAmount } from 'src/common/helpers/generator/monetaryAmount.helper'
import { partners } from 'src/common/helpers/mappings/partners.mapping'
import { useAxios } from 'src/services/__vue_query__/api/fetchByApiClient'
import { Endpoints } from 'src/services/__vue_query__/endpoints'

interface IFont {
  font_size: number
  font_weight: number
  title: string
}

interface IState {
  availabilityMode: 'item' | 'price'
  isItemsAvailabilityDialogOpen: boolean
  itemsAvailabilityOptions: { maxColumnCount: number; defaultColumns: string[] }
}

export default function useBaseTemplateItemTextSettingHook() {
  const templateStore = useTemplateStore()
  const { t: globalT } = translate(globalTranslateObject)
  const { t } = translate(translationObject)
  const { currency_code } = inject<{ currency_code: string }>('ctx')!
  const authStore = useAuthStore()
  const branchesStore = useBranchesStore()

  /* --------------------------- CONSTANT VARIABLES --------------------------- */
  const textAligns = ['left', 'center', 'right']

  const fonts: IFont[] = [
    { font_size: 32, font_weight: 700, title: 'Headline 1' },
    { font_size: 24, font_weight: 700, title: 'Headline 2' },
    { font_size: 18, font_weight: 700, title: 'Headline 3' },
    { font_size: 16, font_weight: 500, title: 'Body 1' },
    { font_size: 13, font_weight: 500, title: 'Body 2' },
    { font_size: 11, font_weight: 500, title: 'Body 3' },
  ]

  const fontFamilies = [
    { value: 'Poppins', label: 'Poppins' },
    { value: 'sans-serif', label: 'Sans Serif' },
  ]

  const tableColumnClasses = {
    headerClasses: 'table-column-fit-content q-px-sm',
    labelClasses: 'fs-13px fw-500 text-cool-gray-100',
  }

  const state = reactive<IState>({
    availabilityMode: 'item',
    isItemsAvailabilityDialogOpen: false,
    itemsAvailabilityOptions: {
      maxColumnCount: 5,
      defaultColumns: ['item_name', 'category_name', 'plu', 'inventory_unit_name', 'default_price'],
    },
  })

  const itemsAvailabilityOptions: IBaseTableDialogOptions = {
    endpoint: 'inventory/item/select-item-list',
    dropdown: {
      endpoint: 'inventory/category/portal/list',
      filter: { store_id: branchesStore.getStoreId },
      allLabel: globalT('all_categories'),
      placeholder: t('contents.texts.items_table_options.placeholder'),
      payloadProperty: 'category_id',
      bind: {
        label: 'name',
        value: 'id',
        statusColor: null,
        status: false,
        icon: null,
      },
    },
    filter: {
      item_kind: [0, 1, 2, 4],
      store_id: branchesStore.getStoreId,
    },
    rowKey: 'item_id',
    columns: [
      {
        name: 'id',
        align: 'left',
        label: globalT('id'),
        field: 'item_id',
        type: 'text',
        ...tableColumnClasses,
      },
      {
        label: t('contents.texts.items_table_options.columns.item_name'),
        align: 'left',
        field: 'item_name',
        name: 'item_name',
        style: 'width: 35%',
        rowClasses: 'fs-15px text-cool-gray-80',
        ...tableColumnClasses,
      },
      {
        label: t('contents.texts.items_table_options.columns.item_category'),
        align: 'left',
        field: 'category_name',
        name: 'category_name',
        style: 'width: 35%',
        slot: 'category',
        rowClasses: 'fs-15px text-cool-gray-80',
        ...tableColumnClasses,
      },
      {
        label: 'PLU',
        align: 'left',
        field: 'plu',
        name: 'plu',
        style: 'width: 10%',
        rowClasses: 'fs-15px text-cool-gray-80',
        ...tableColumnClasses,
      },
      {
        label: globalT('unit'),
        align: 'left',
        field: 'inventory_unit_name',
        name: 'inventory_unit_name',
        style: 'width: 10%',
        rowClasses: 'fs-15px text-cool-gray-80',
        ...tableColumnClasses,
      },
      {
        label: globalT('price'),
        align: 'left',
        field: 'default_price',
        name: 'default_price',
        format: (value: number | string) => formatMonetaryAmount(value, currency_code),
        style: 'width: 10%',
        rowClasses: 'fs-15px text-cool-gray-80',
        ...tableColumnClasses,
      },
      {
        label: globalT('image'),
        align: 'left',
        field: (row: ITableRow) => row,
        name: 'image',
        format: (row: { item_id: number; images: string[] }) =>
          getImagePath({ images: row.images, id: row.item_id }, 'item', authStore.portalUser.business_id),
        style: 'width: 82px',
        type: 'image',
        ...tableColumnClasses,
      },
      {
        name: 'partner_id',
        align: 'left',
        label: globalT('partner_id'),
        field: 'id2',
        type: 'text',
        ...tableColumnClasses,
      },
      {
        name: 'partner_name',
        align: 'left',
        label: globalT('partner_name'),
        field: 'id2_type',
        format: (value: number) => partners.get(value)?.name ?? '',
        type: 'text',
        ...tableColumnClasses,
      },
      {
        name: 'layout',
        align: 'center',
        separator: false,
      },
    ],
    noData: { title: '', description: '' },
  }
  /* -------------------------------------------------------------------------- */

  const isFontPickerOpen = ref(false)

  // this watch use for resetting the text value when the is_field changed
  // Using setImmediate because this watch is triggered before rendering k-input with is_false set to false, causing it to render [object Object] instead of an empty string //
  watch(
    () => [templateStore.activeItem, templateStore.activeItem?.is_field],
    (newValue, oldValue) => {
      const [newActiveItem, newIsField] = newValue as [ITemplateLayerItem, boolean]
      const [previousActiveItem, previousIsField] = oldValue as [ITemplateLayerItem, boolean]

      if (newActiveItem.id === previousActiveItem.id && newIsField !== previousIsField)
        setImmediate(() => templateStore.handleUpdateItemSetting('value', ''))
    },
    { deep: true },
  )

  watch(
    () => templateStore.activeItem?.value,
    () => {
      if (!templateStore.activeItem?.value) return

      if (typeof templateStore.activeItem.value === 'string') return

      const { field } = templateStore.activeItem.value as ITemplateItemFieldValue

      const request = useAxios(Endpoints.INVENTORY_MODIFIER_CATEGORY_PORTAL_LIST, { filter: { item_id: field } })
      request.fetch()
    },
    { immediate: true, deep: true },
  )

  function handlePreSetTextStyle(font: IFont) {
    templateStore.handleUpdateItemSetting('font_size', font.font_size)
    templateStore.handleUpdateItemSetting('font_weight', font.font_weight)
  }

  function addItemAvailability(selectedItem: IItemSummary) {
    if (state.availabilityMode === 'item')
      templateStore.handleUpdateItemSetting('value', {
        field: selectedItem.item_id,
        title: selectedItem.item_name,
      })
    else
      templateStore.handleUpdateItemSetting('value', {
        field: selectedItem.item_id,
        title: `${selectedItem.item_name} - Price`,
        modifiers: [],
      })
    state.isItemsAvailabilityDialogOpen = false
  }

  function handleOpenAvailabilityDialog(mode: 'item' | 'price') {
    state.availabilityMode = mode
    state.isItemsAvailabilityDialogOpen = true
  }
  return {
    state,
    branchesStore,
    templateStore,
    isFontPickerOpen,
    textAligns,
    fonts,
    fontFamilies,
    itemsAvailabilityOptions,
    addItemAvailability,
    handlePreSetTextStyle,
    handleOpenAvailabilityDialog,
  }
}
